import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  Switch,
  Typography,
} from "components";
import { useFormik } from "formik";
import { useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { portfolioOfferings } from "utils/PortfolioOfferings";

interface PortfolioOfferingsFormValues {
  [key: string]: any;
  holding: boolean;
  insights: boolean;
  nav: boolean;
  benchmarkComparison: boolean;
}

const PortfolioOfferings = () => {
  const { id } = useParams();

  const [expandedPortfolio, setExpandedPortfolio] = useState<number>(-1);

  const { data, refetch, isFetched } = useQuery({
    queryKey: ["GET_PORTFOLIO_OFFERINGS"],
    queryFn: () =>
      axiosInstance.get(`/super_admin/${id}/get_portfolio_offerings/`),
  });

  const { mutate } = useMutation({
    mutationFn: (data: any) =>
      axiosInstance.patch(`/super_admin/${id}/portfolio_offerings/`, data),
    onSuccess: (response) => {
      refetch();
      toast.success(response?.data?.message || "Broker Update Successfully");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const formik = useFormik<PortfolioOfferingsFormValues>({
    enableReinitialize: isFetched,
    initialValues: {
      holding: data?.data?.data?.portfolioOfferings?.holding || false,
      insights: data?.data?.data?.portfolioOfferings?.insights || false,
      nav: data?.data?.data?.portfolioOfferings?.nav || false,
      benchmarkComparison:
        data?.data?.data?.portfolioOfferings?.benchmarkComparison || false,
    },

    onSubmit: (values) => {
      mutate(values);
    },
  });

  return (
    <form id="portfolio-offerings-form" onSubmit={formik.handleSubmit}>
      {portfolioOfferings.map((dataItem, index) => (
        <Accordion
          key={`portfolio-${index}`}
          disableGutters
          onChange={() => {
            setExpandedPortfolio(index === expandedPortfolio ? -1 : index);
          }}
          expanded={index === expandedPortfolio}
          style={{
            border: `solid 1px var(--neutral-100)`,
            borderRadius: "8px",
            boxShadow: "none",
            background: "none",
          }}
        >
          <AccordionSummary
            expandIcon={<GridExpandMoreIcon />}
            aria-controls={`portfolio-panel${index + 1}-content`}
            id={`portfolio-panel${index + 1}-header`}
            sx={{
              "& .MuiAccordionSummary-content": {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              },
            }}
          >
            <Typography
              fontSize={16}
              fontWeight={600}
              textTransform="none"
              color={"var(--primary-900)"}
            >
              {dataItem.mainHeading}
            </Typography>
            {expandedPortfolio === index && (
              <Button
                type="submit"
                variant="contained"
                size="medium"
                form="portfolio-offerings-form"
                onClick={(e) => e.stopPropagation()}
              >
                Save
              </Button>
            )}
          </AccordionSummary>
          <Divider />
          {dataItem.details.map((data, subIndex) => (
            <AccordionDetails
              key={`portfolio-detail-${subIndex}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Switch
                name={data.key}
                onChange={(event) => {
                  formik.setFieldValue(data.key, event.target.checked);
                }}
                checked={formik.values[data.key] === true}
              />
              <Typography
                fontSize={14}
                fontWeight={500}
                textTransform="none"
                color={"var(--black-900)"}
              >
                {data.heading}
              </Typography>
            </AccordionDetails>
          ))}
        </Accordion>
      ))}
    </form>
  );
};

export default PortfolioOfferings;
